import { ApplicationCodes, DocumentationDto, GuidepostDto } from "Api/Api";
import { Divider, List, ListItem, Typography } from "@material-ui/core";
import React, { useEffect } from "react";
import { Resources, useResource } from "Translations/Resources";

import Cookies from "universal-cookie";
import { DefaultButtonWithTooltip } from "Components/Shared/Buttons/DefaultButtonWithTooltip";
import { GuidepostApplication } from "Components/Guidepost/GuidepostApplication";
import { GuidepostIcon } from "Components/Shared/Icons/GuidepostIcon";
import { InfoIcon } from "Components/Shared/Icons/InfoIcon";
import { LogoutIcon } from "Components/Shared/Icons/LogoutIcon";
import { PrimaryTooltip } from "Components/Shared/PrimaryTooltip";
import { isNullOrEmpty } from "Utils/StringUtils";
import { resetUser } from "State/Auth/AuthReducer";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useIsInsideIbis } from "Hooks/Guidepost/useIsInsideIbis";

type Props = {
  guidepost: GuidepostDto;
  documentation: DocumentationDto | null;
  setDetailOpen: (val: boolean) => void;
  handleLogout?: () => void;
  showTooltips?: boolean;
};

const StyledWrapper = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 50px;
  padding-bottom: ${props => props.theme.spacing(1)}px;
  background-color: ${props => props.theme.colors.white};
  z-index: 1;
  box-shadow: ${props => props.theme.shadow.primary};
  display: grid;
  grid-template-rows: 1fr auto;
  justify-content: center;

  hr {
    margin-top: ${props => props.theme.spacing(1.5)}px;
    margin-left: ${props => props.theme.spacing(1.25)}px;
    margin-right: ${props => props.theme.spacing(1.25)}px;
  }

  ${props => props.theme.breakpoints.down("xs")} {
    hr {
      margin: 0;
    }
    hr ~ div {
      display: none;
    }
    button {
      display: none;
    }
  }
`;

const StyledItem = styled.div`
  margin-top: ${props => props.theme.spacing(1)}px;
  display: flex;

  a {
    color: inherit;
    text-decoration: inherit;
  }

  .ibis-MuiListItemIcon-root {
    min-width: 0;
  }

  .ibis-MuiListItem-button {
    padding: ${props => props.theme.spacing(0.5)}px;
    border-radius: ${props => props.theme.borderRadius};
  }

  svg {
    width: 30px;
    height: 30px;
  }
`;

const StyledAppHeader = styled(Typography)`
  display: none;
  color: ${props => props.theme.palette.secondary.main};
  line-height: 2;
  margin-left: ${props => props.theme.spacing(2)}px;
  ${props => props.theme.breakpoints.down("xs")} {
    display: inline;
  }
`;

const StyledIcon = styled.div`
  display: flex;
  svg {
    width: 20px;
    height: auto;
    max-height: 34px;
  }
  margin: 0 auto;

  .guidepost-icon {
    width: 23px;
    height: 23px;
  }
`;

const StyledLink = styled.a`
  display: block;
  padding: ${props => props.theme.spacing(1, 0)};

  svg {
    display: block;
  }
`;

let wasAutologoutInitialized = false;

export const isEmptyDocumentation = (documentation: DocumentationDto | null) =>
  isNullOrEmpty(documentation?.title) ||
  isNullOrEmpty(documentation?.documentationUrl);

export const GuidePostPanel: React.FunctionComponent<Props> = props => {
  const { t } = useResource();
  const {
    guidepost,
    documentation,
    setDetailOpen,
    handleLogout,
    showTooltips,
  } = props;
  const isInsideIbis = useIsInsideIbis();
  const category = guidepost && guidepost.categories && guidepost.categories.find(e => e.category.name === "Interní aplikace");
  const applications = category && category.applications;

  const dispatch = useDispatch();
  useEffect(() => {
    if (isAutologoutNeeded(isInsideIbis)) {
      wasAutologoutInitialized = true;
      toast.warning(
        "Za několik sekund dojde k automatickému jednorázovému odhlášení z důvodu opravy cookies.",
        {
          autoClose: 4000,
        },
      );
      setTimeout(function () {
        dispatch(resetUser());
      }, 4000);
    }
  });

  return (
    <React.Fragment>
      <StyledWrapper className="ibis-guidepost-panel">
        <List className="ibis-guidepost-list">
          <StyledItem
            onClick={() => setDetailOpen(true)}
            className="ibis-guidepost-toggle"
          >
            <PrimaryTooltip
              title={t(Resources.MenuItems.Guidepost)}
              placement={"right"}
            >
              <ListItem button>
                <StyledIcon>
                  <GuidepostIcon className={"guidepost-icon"} />
                </StyledIcon>
              </ListItem>
            </PrimaryTooltip>
          </StyledItem>

          <Divider />
          <StyledAppHeader variant={"h2"}>
            {t(Resources.Common.App.Name)}
          </StyledAppHeader>

          <GuidepostApplication
            key={ApplicationCodes.Dashboard}
            application={{
              applicationCode: ApplicationCodes.Dashboard,
              name: t(Resources.MenuItems.Dashboard),
              url: `/${t(Resources.Routes.Dashboard)}`,
              imageUri: "",
            }}
            logVisit={false}
            showTooltips={showTooltips}
          />
          
          {applications && applications?.map(app => (
            <GuidepostApplication
              key={app.applicationCode}
              application={app}
              logVisit
              showTooltips={showTooltips}
            />
          ))}
        </List>

        {documentation && !isEmptyDocumentation(documentation) && (
          <DefaultButtonWithTooltip
            title={documentation.title}
            placement={"right"}
          >
            <StyledLink
              href={documentation.documentationUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <InfoIcon width="30" height="30" />
            </StyledLink>
          </DefaultButtonWithTooltip>
        )}

        {handleLogout && (
          <DefaultButtonWithTooltip
            title={t(Resources.Auth.Logout)}
            placement={"right"}
            onClick={handleLogout}
          >
            <LogoutIcon />
          </DefaultButtonWithTooltip>
        )}
      </StyledWrapper>
    </React.Fragment>
  );
};

const isAutologoutNeeded = (isInsideIbis: boolean): boolean => {
  if (!isInsideIbis) {
    return false;
  }

  const cookieName = process.env.REACT_APP_IBIS_TOKEN_NAME as string;
  const cookie = new Cookies().get(cookieName);

  return cookie === undefined && !wasAutologoutInitialized;
};
